import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { NumericInput } from './ui/numeric-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Users } from 'lucide-react';

function CustomerListTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    firm_name: '',
    customer_name: '',
    credit_limit: 0,
    payment_cycle: 30,
    mobile: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data.customers);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up formData - convert empty email to null
      const submitData = {
        ...formData,
        email: formData.email?.trim() || null
      };
      
      if (editMode && selectedCustomer) {
        await axios.put(`${API}/customers/${selectedCustomer.id}`, submitData);
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, submitData);
        toast.success('Customer added');
      }
      fetchCustomers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      firm_name: '',
      customer_name: '',
      credit_limit: 0,
      payment_cycle: 30,
      mobile: '',
      email: '',
      address: ''
    });
    setEditMode(false);
    setSelectedCustomer(null);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      firm_name: customer.firm_name,
      customer_name: customer.customer_name,
      credit_limit: customer.credit_limit,
      payment_cycle: customer.payment_cycle,
      mobile: customer.mobile,
      email: customer.email || '',
      address: customer.address
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="customer-list-tab">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="font-medium">Customer List</h4>
          <p className="text-sm text-muted">Manage your customers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Firm Name *</label>
                  <Input
                    data-testid="firm-name-input"
                    value={formData.firm_name}
                    onChange={(e) => setFormData({ ...formData, firm_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Name *</label>
                  <Input
                    data-testid="customer-name-input"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Credit Limit *</label>
                  <NumericInput
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Payment Cycle (days) *</label>
                  <NumericInput
                    value={formData.payment_cycle}
                    onChange={(e) => setFormData({ ...formData, payment_cycle: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mobile *</label>
                  <Input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Address *</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="customer-submit-button">
                  {editMode ? 'Update' : 'Add'} Customer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-lg">
        {loading ? (
          <div className="p-8 text-center text-muted">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-muted">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No customers yet. Add your first customer!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider">
                    Firm Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider">
                    Credit Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider">
                    Mobile
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-secondary/50" data-testid="customer-row">
                    <td className="px-6 py-4 text-sm font-medium">{customer.firm_name}</td>
                    <td className="px-6 py-4 text-sm">{customer.customer_name}</td>
                    <td className="px-6 py-4 text-sm">₹{customer.credit_limit.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm">{customer.mobile}</td>
                    <td className="px-6 py-4 text-sm">
                      <Button
                        data-testid="edit-customer-button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerListTab;
